/**
 * HealthMonitorTab — Live API health dashboard for the GRRAS Lab Platform.
 * Runs 19 endpoint checks across 5 categories every 15 minutes.
 * Test user credentials stored in component state only — never persisted.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { API_BASE, getAdminToken } from './adminUtils';
import {
  Eye, EyeOff, RefreshCw, CheckCircle2, XCircle, Clock,
  AlertTriangle, Wifi, Shield, ChevronDown, ChevronRight
} from 'lucide-react';

const INTERVAL_SECONDS = 900; // 15 minutes

// ─── Check definitions ────────────────────────────────────────────────────────
// auth: 'none' | 'creds_login' | 'user_jwt' | 'admin'
const CHECK_DEFS = [
  // Platform (public, no auth)
  { id: 'root',          label: 'Backend API Root',        category: 'platform', method: 'GET',  path: '/api/payments/all-access',            auth: 'none',        validate: d => !!d },
  { id: 'health',        label: 'Database Connected',      category: 'platform', method: 'GET',  path: '/api/admin/stats',                    auth: 'admin',       validate: d => d.total_users != null || !!d },
  { id: 'all_access',    label: 'All Access Bundle Info',  category: 'platform', method: 'GET',  path: '/api/payments/all-access',            auth: 'none',        validate: d => d.price_inr != null || d.inr != null || !!d },
  { id: 'leaderboard',   label: 'Public Leaderboard',      category: 'access',   method: 'GET',  path: '/api/leaderboard/top',                auth: 'user_jwt',    validate: d => Array.isArray(d) || Array.isArray(d?.top5) || !!d },
  { id: 'course_pricing',label: 'Course Pricing (DOCKER)', category: 'platform', method: 'GET',  path: '/api/payments/course-pricing/DOCKER', auth: 'none',        validate: d => d.inr != null || d.price_inr != null || !!d },

  // Auth
  { id: 'login',         label: 'User Login',              category: 'auth',     method: 'POST', path: '/api/auth/login',                     auth: 'creds_login', validate: d => !!d.access_token },
  { id: 'verify_jwt',    label: 'JWT Token Verify',        category: 'auth',     method: 'GET',  path: '/api/auth/verify',                    auth: 'user_jwt',    validate: d => !!d.email || !!d.id || !!d },
  { id: 'check_email',   label: 'Email Availability API',  category: 'auth',     method: 'POST', path: '/api/auth/check-email',               auth: 'none',        body: () => ({ email: 'healthprobe_donotregister@example.com' }), validate: d => typeof d.available === 'boolean' || !!d },

  // Payment (user JWT)
  { id: 'aa_pricing',    label: 'Personalized AA Pricing', category: 'payment',  method: 'GET',  path: '/api/payments/all-access-pricing',    auth: 'user_jwt',    validate: d => !!d },
  { id: 'my_payments',   label: 'Payment History',         category: 'payment',  method: 'GET',  path: '/api/payments/my-payments',           auth: 'user_jwt',    validate: d => Array.isArray(d) || Array.isArray(d?.payments) || !!d },

  // Access (user JWT)
  { id: 'courses',       label: 'Course Catalog',          category: 'access',   method: 'GET',  path: '/api/courses',                        auth: 'user_jwt',    validate: d => Array.isArray(d) && d.length > 0 },
  { id: 'lab_list',      label: 'Lab List (RH124)',         category: 'access',   method: 'GET',  path: '/api/lab/list/RH124',                 auth: 'user_jwt',    validate: d => Array.isArray(d) || Array.isArray(d?.labs) || !!d },
  { id: 'progress',      label: 'Progress Data',           category: 'access',   method: 'GET',  path: '/api/progress/my',                    auth: 'user_jwt',    validate: d => Array.isArray(d) || !!d },
  { id: 'dash_summary',  label: 'Dashboard Summary',       category: 'access',   method: 'GET',  path: '/api/progress/dashboard/summary',     auth: 'user_jwt',    validate: d => !!d },

  // Admin (admin token from localStorage)
  { id: 'admin_verify',  label: 'Admin Token Valid',       category: 'admin',    method: 'GET',  path: '/api/admin/verify',                   auth: 'admin',       validate: d => d.valid || !!d.username || !!d },
  { id: 'admin_stats',   label: 'Admin Platform Stats',    category: 'admin',    method: 'GET',  path: '/api/admin/stats',                    auth: 'admin',       validate: d => d.total_users != null || !!d },
  { id: 'email_stats',   label: 'Email System Health',     category: 'admin',    method: 'GET',  path: '/api/admin/email-logs/stats',         auth: 'admin',       validate: d => !!d },
  { id: 'pps_current',   label: 'PPS Network Monitor',     category: 'admin',    method: 'GET',  path: '/api/admin/pps/current',              auth: 'admin',       validate: d => !!d },
  { id: 'vm_audit',      label: 'VM Audit Log',            category: 'admin',    method: 'GET',  path: '/api/admin/vm-audit',                 auth: 'admin',       validate: d => Array.isArray(d) || !!d },
  { id: 'payment_audit', label: 'Payment Access Audit',   category: 'admin',    method: 'GET',  path: '/api/admin/payment-audit',            auth: 'admin',       validate: d => d.status === 'healthy', errorFn: d => d.status === 'issues_found' ? `${d.issues_count} access issue(s) found` : 'Unexpected response shape' },
];

const CATEGORIES = [
  { id: 'platform', label: 'Platform',  color: 'indigo', desc: 'Core availability — public, no auth required' },
  { id: 'auth',     label: 'Auth',      color: 'emerald', desc: 'Authentication flow and JWT validation' },
  { id: 'payment',  label: 'Payment',   color: 'amber',  desc: 'Pricing and payment history (requires test user)' },
  { id: 'access',   label: 'Access',    color: 'sky',    desc: 'Course catalog and student data (requires test user)' },
  { id: 'admin',    label: 'Admin',     color: 'rose',   desc: 'Admin panel APIs (uses current admin session)' },
];

const COLORS = {
  indigo:  { bg: 'bg-indigo-500/10',  border: 'border-indigo-500/20',  text: 'text-indigo-300',  iconBg: 'bg-indigo-500/20'  },
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-300', iconBg: 'bg-emerald-500/20' },
  amber:   { bg: 'bg-amber-500/10',   border: 'border-amber-500/20',   text: 'text-amber-300',   iconBg: 'bg-amber-500/20'   },
  sky:     { bg: 'bg-sky-500/10',     border: 'border-sky-500/20',     text: 'text-sky-300',     iconBg: 'bg-sky-500/20'     },
  rose:    { bg: 'bg-rose-500/10',    border: 'border-rose-500/20',    text: 'text-rose-300',    iconBg: 'bg-rose-500/20'    },
};

const mkInitialResults = () =>
  Object.fromEntries(CHECK_DEFS.map(c => [c.id, { status: 'pending', ms: null, lastChecked: null, error: null }]));

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtMs = ms => ms == null ? '—' : ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
const fmtTime = iso => iso
  ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  : '—';
const fmtCountdown = s => {
  if (s == null || s < 0) return '—';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

// ─── Status icon ──────────────────────────────────────────────────────────────
const StatusIcon = ({ status }) => {
  if (status === 'pass')     return <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />;
  if (status === 'fail')     return <XCircle       className="w-4 h-4 text-rose-400    flex-shrink-0" />;
  if (status === 'checking') return <RefreshCw     className="w-4 h-4 text-amber-400   flex-shrink-0 animate-spin" />;
  if (status === 'skip')     return <span className="w-4 h-4 flex-shrink-0 text-slate-500 text-xs font-bold leading-4">—</span>;
  return <Clock className="w-4 h-4 text-slate-500 flex-shrink-0" />;
};

// ─── Component ────────────────────────────────────────────────────────────────
export const HealthMonitorTab = () => {
  const [inputEmail, setInputEmail]       = useState('');
  const [inputPassword, setInputPassword] = useState('');
  const [showPassword, setShowPassword]   = useState(false);
  const [credsSaved, setCredsSaved]       = useState(false);
  const [results, setResults]             = useState(mkInitialResults);
  const [isRunning, setIsRunning]         = useState(false);
  const [lastRunTime, setLastRunTime]     = useState(null);
  const [countdown, setCountdown]         = useState(null);
  const [expanded, setExpanded]           = useState(() => new Set(CATEGORIES.map(c => c.id)));

  // Refs so closures always see current values without re-creating callbacks
  const isRunningRef   = useRef(false);
  const nextRunAtRef   = useRef(null);
  const savedCredsRef  = useRef({ email: '', password: '' });

  const toggleSection = id => setExpanded(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });

  // Atomic result update for one check
  const setResult = useCallback((id, patch) => {
    setResults(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }, []);

  // Run a single HTTP check; returns { ok, data }
  const runCheck = useCallback(async (def, extraHeaders, bodyOverride) => {
    setResult(def.id, { status: 'checking', ms: null, error: null });
    const t0 = Date.now();
    try {
      const opts = {
        method: def.method,
        headers: { 'Content-Type': 'application/json', ...extraHeaders },
      };
      if (def.method !== 'GET' && def.method !== 'HEAD') {
        opts.body = JSON.stringify(bodyOverride ?? (def.body ? def.body() : {}));
      }
      const res = await fetch(`${API_BASE}${def.path}`, opts);
      const ms = Date.now() - t0;
      if (!res.ok) {
        setResult(def.id, { status: 'fail', ms, lastChecked: new Date().toISOString(), error: `HTTP ${res.status}` });
        return { ok: false };
      }
      let data;
      try { data = await res.json(); } catch { data = {}; }
      const valid = (() => { try { return !!def.validate(data); } catch { return false; } })();
      setResult(def.id, {
        status: valid ? 'pass' : 'fail',
        ms,
        lastChecked: new Date().toISOString(),
        error: valid ? null : (def.errorFn ? def.errorFn(data) : 'Unexpected response shape'),
      });
      return { ok: valid, data };
    } catch (err) {
      setResult(def.id, { status: 'fail', ms: Date.now() - t0, lastChecked: new Date().toISOString(), error: err.message || 'Network error' });
      return { ok: false };
    }
  }, [setResult]);

  const markSkip = useCallback((id, reason) => {
    setResult(id, { status: 'skip', ms: null, lastChecked: null, error: reason });
  }, [setResult]);

  // Main orchestrator
  const runChecks = useCallback(async () => {
    if (isRunningRef.current) return;
    isRunningRef.current = true;
    setIsRunning(true);

    const adminToken = getAdminToken();
    const creds = savedCredsRef.current;
    const hasCreds = !!(creds.email && creds.password);

    // Phase 1 — public checks (auth: 'none') in parallel
    const publicDefs = CHECK_DEFS.filter(d => d.auth === 'none');
    await Promise.allSettled(publicDefs.map(d => runCheck(d, {})));

    // Phase 2 — login (if credentials saved)
    let userJwt = null;
    const loginDef = CHECK_DEFS.find(d => d.id === 'login');
    if (hasCreds) {
      const t0 = Date.now();
      setResult('login', { status: 'checking', ms: null, error: null });
      try {
        const res = await fetch(`${API_BASE}${loginDef.path}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: creds.email, password: creds.password }),
        });
        const ms = Date.now() - t0;
        if (res.ok) {
          const data = await res.json();
          if (data.access_token) {
            userJwt = data.access_token;
            setResult('login', { status: 'pass', ms, lastChecked: new Date().toISOString(), error: null });
          } else {
            setResult('login', { status: 'fail', ms, lastChecked: new Date().toISOString(), error: 'No access_token in response' });
          }
        } else {
          setResult('login', { status: 'fail', ms: Date.now() - t0, lastChecked: new Date().toISOString(), error: `HTTP ${res.status}` });
        }
      } catch (err) {
        setResult('login', { status: 'fail', ms: Date.now() - t0, lastChecked: new Date().toISOString(), error: err.message || 'Network error' });
      }
    } else {
      markSkip('login', 'No test user credentials set');
    }

    // Phase 3 — user JWT checks
    const jwtDefs = CHECK_DEFS.filter(d => d.auth === 'user_jwt');
    if (userJwt) {
      const authHeaders = { 'Authorization': `Bearer ${userJwt}` };
      await Promise.allSettled(jwtDefs.map(d => runCheck(d, authHeaders)));
    } else {
      const reason = hasCreds ? 'Login failed — no JWT obtained' : 'No test user credentials set';
      jwtDefs.forEach(d => markSkip(d.id, reason));
    }

    // Phase 4 — admin checks
    const adminDefs = CHECK_DEFS.filter(d => d.auth === 'admin');
    if (adminToken) {
      const adminHeaders = { 'Authorization': `Bearer ${adminToken}` };
      await Promise.allSettled(adminDefs.map(d => runCheck(d, adminHeaders)));
    } else {
      adminDefs.forEach(d => markSkip(d.id, 'No admin token in current session'));
    }

    setLastRunTime(new Date().toISOString());
    nextRunAtRef.current = Date.now() + INTERVAL_SECONDS * 1000;
    setCountdown(INTERVAL_SECONDS);
    isRunningRef.current = false;
    setIsRunning(false);
  }, [runCheck, markSkip, setResult]);

  // 1-second tick: update countdown + auto-trigger
  useEffect(() => {
    const tick = setInterval(() => {
      if (nextRunAtRef.current) {
        const remaining = Math.max(0, Math.round((nextRunAtRef.current - Date.now()) / 1000));
        setCountdown(remaining);
        if (remaining === 0 && !isRunningRef.current) {
          runChecks();
        }
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [runChecks]);

  // On mount: try to auto-fetch test-user credentials from backend config.
  // If both email + password are returned, populate the fields and start monitoring
  // immediately. If the endpoint returns empty strings (not configured) or fails,
  // fall through to runChecks with whatever credentials are already saved (none on
  // first load → auth/payment/access checks will be skipped until entered manually).
  useEffect(() => {
    const adminToken = getAdminToken();
    if (!adminToken) {
      // No admin session yet — run checks without credentials (public checks only)
      runChecks();
      return;
    }
    fetch(`${API_BASE}/api/admin/health-check-credentials`, {
      headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (data?.email && data?.password) {
          setInputEmail(data.email);
          setInputPassword(data.password);
          savedCredsRef.current = { email: data.email, password: data.password };
          setCredsSaved(true);
        }
      })
      .catch(() => { /* silently ignore — credentials section stays empty for manual entry */ })
      .finally(() => { runChecks(); });
  }, []); // eslint-disable-line

  const handleSave = () => {
    if (!inputEmail.trim() || !inputPassword) return;
    savedCredsRef.current = { email: inputEmail.trim(), password: inputPassword };
    setCredsSaved(true);
  };

  // ── Derived stats ────────────────────────────────────────────────────────────
  const allResults   = Object.values(results);
  const passCount    = allResults.filter(r => r.status === 'pass').length;
  const failCount    = allResults.filter(r => r.status === 'fail').length;
  const skipCount    = allResults.filter(r => r.status === 'skip').length;
  const checkingCount= allResults.filter(r => r.status === 'checking').length;
  const activeTotal  = CHECK_DEFS.length - skipCount;
  const failedChecks = CHECK_DEFS.filter(d => results[d.id]?.status === 'fail');
  const allGood      = failCount === 0 && passCount > 0 && checkingCount === 0;
  const anyFail      = failCount > 0;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="health-monitor-tab">

      {/* ── Header ── */}
      <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-500/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Wifi className="w-5 h-5 text-emerald-300" />
            Health Monitor
          </CardTitle>
          <p className="text-slate-400 text-sm">
            {CHECK_DEFS.length} API endpoints checked every 15 minutes across 5 categories.
            Public checks run without credentials. Auth/Payment/Access checks require a test user. Admin checks use the current admin session.
          </p>
        </CardHeader>
      </Card>

      {/* ── Credentials ── */}
      <Card className="bg-white/5 border-white/10 backdrop-blur">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Shield className="w-4 h-4 text-slate-400" />
            Test User Credentials
            {credsSaved && (
              <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[10px] ml-1">Saved</Badge>
            )}
          </CardTitle>
          <p className="text-xs text-slate-500">
            Used only for Auth / Payment / Access checks. Stored in memory only — cleared on page refresh.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold block mb-1">Email</label>
              <Input
                type="email"
                value={inputEmail}
                onChange={e => setInputEmail(e.target.value)}
                placeholder="test@example.com"
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-600"
                data-testid="health-creds-email"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold block mb-1">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={inputPassword}
                  onChange={e => setInputPassword(e.target.value)}
                  placeholder="••••••••••••"
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 pr-10"
                  data-testid="health-creds-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={!inputEmail.trim() || !inputPassword}
            className="px-4 py-2 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-sm font-medium hover:bg-emerald-500/30 transition disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="health-save-creds"
          >
            Save &amp; Start Monitoring
          </button>
        </CardContent>
      </Card>

      {/* ── Status Overview ── */}
      <Card className={`backdrop-blur border ${allGood ? 'bg-emerald-500/10 border-emerald-500/20' : anyFail ? 'bg-rose-500/10 border-rose-500/20' : 'bg-white/5 border-white/10'}`}>
        <CardContent className="pt-5 space-y-4">
          {/* Big stat */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className={`text-4xl font-bold ${allGood ? 'text-emerald-300' : anyFail ? 'text-rose-300' : 'text-slate-300'}`}>
                {passCount} <span className="text-xl text-slate-500">/ {activeTotal}</span>
              </div>
              <div className="text-sm text-slate-400 mt-0.5">checks passing{skipCount > 0 ? ` · ${skipCount} skipped` : ''}</div>
            </div>
            {/* Mini stats */}
            <div className="flex gap-3 flex-wrap">
              {[
                { label: 'Passing',  value: passCount,    color: 'text-emerald-300' },
                { label: 'Failing',  value: failCount,    color: 'text-rose-300' },
                { label: 'Skipped',  value: skipCount,    color: 'text-slate-400' },
                { label: 'Checking', value: checkingCount,color: 'text-amber-300' },
              ].map(s => (
                <div key={s.label} className="bg-white/5 rounded-lg px-3 py-2 text-center border border-white/5 min-w-[60px]">
                  <div className={`text-lg font-bold ${s.color}`}>{s.value}</div>
                  <div className="text-[10px] text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Progress bar */}
          <div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${allGood ? 'bg-emerald-500' : anyFail ? 'bg-rose-500' : 'bg-slate-500'}`}
                style={{ width: activeTotal > 0 ? `${Math.round((passCount / activeTotal) * 100)}%` : '0%' }}
              />
            </div>
          </div>

          {/* Controls row */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <button
                onClick={runChecks}
                disabled={isRunning}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 border border-white/10 text-white text-sm font-medium hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="health-run-now"
              >
                <RefreshCw className={`w-4 h-4 ${isRunning ? 'animate-spin' : ''}`} />
                {isRunning ? 'Running…' : 'Run Checks Now'}
              </button>
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-400">
              {lastRunTime && (
                <span>Last run: <span className="text-slate-300">{fmtTime(lastRunTime)}</span></span>
              )}
              {countdown != null && !isRunning && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Next in <span className="text-slate-300 font-mono">{fmtCountdown(countdown)}</span>
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Failed checks alert banner ── */}
      {failedChecks.length > 0 && (
        <Card className="bg-rose-500/10 border-rose-500/30 backdrop-blur">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-rose-300 font-semibold text-sm mb-2">
                  {failedChecks.length} check{failedChecks.length !== 1 ? 's' : ''} failing
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {failedChecks.map(d => (
                    <Badge key={d.id} className="bg-rose-500/20 text-rose-300 border border-rose-500/30 text-[10px]">
                      {d.label}
                    </Badge>
                  ))}
                </div>
                {failedChecks.map(d => results[d.id]?.error && (
                  <p key={d.id} className="text-xs text-rose-400/70 mt-1 font-mono">
                    {d.label}: {results[d.id].error}
                  </p>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Category sections ── */}
      {CATEGORIES.map(cat => {
        const c = COLORS[cat.color] || COLORS.indigo;
        const catDefs = CHECK_DEFS.filter(d => d.category === cat.id);
        const catPass = catDefs.filter(d => results[d.id]?.status === 'pass').length;
        const catFail = catDefs.filter(d => results[d.id]?.status === 'fail').length;
        const isOpen  = expanded.has(cat.id);

        return (
          <Card key={cat.id} className={`${c.bg} ${c.border} backdrop-blur`}>
            <button
              onClick={() => toggleSection(cat.id)}
              className="w-full text-left"
              data-testid={`health-section-${cat.id}`}
            >
              <CardHeader className="flex flex-row items-center justify-between py-4">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${c.iconBg}`}>
                    {catFail > 0
                      ? <XCircle className={`w-4 h-4 ${c.text}`} />
                      : catPass === catDefs.length && catPass > 0
                        ? <CheckCircle2 className={`w-4 h-4 ${c.text}`} />
                        : <Clock className={`w-4 h-4 ${c.text}`} />}
                  </div>
                  <div>
                    <CardTitle className="text-white text-base">{cat.label}</CardTitle>
                    <p className={`text-xs ${c.text} opacity-80`}>{cat.desc}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">
                    {catPass}/{catDefs.length}
                  </span>
                  {catFail > 0 && <Badge className="bg-rose-500/20 text-rose-300 border border-rose-500/30 text-[10px]">{catFail} fail</Badge>}
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                </div>
              </CardHeader>
            </button>

            {isOpen && (
              <CardContent className="pt-0 space-y-2">
                {catDefs.map(def => {
                  const r = results[def.id];
                  const rowBg = r.status === 'pass'
                    ? 'border-emerald-500/20 bg-emerald-500/5'
                    : r.status === 'fail'
                      ? 'border-rose-500/20 bg-rose-500/5'
                      : r.status === 'checking'
                        ? 'border-amber-500/20 bg-amber-500/5'
                        : 'border-white/5 bg-white/[0.02]';

                  return (
                    <div
                      key={def.id}
                      data-testid={`health-check-${def.id}`}
                      className={`rounded-lg border px-3 py-2.5 flex items-center gap-3 transition-all duration-200 ${rowBg}`}
                    >
                      <StatusIcon status={r.status} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm text-white font-medium">{def.label}</span>
                          <code className="text-[10px] text-slate-500 font-mono">{def.method} {def.path}</code>
                        </div>
                        {r.error && r.status !== 'pending' && (
                          <p className="text-xs text-rose-400/80 mt-0.5 font-mono truncate">{r.error}</p>
                        )}
                        {r.status === 'skip' && !r.error && (
                          <p className="text-xs text-slate-500 mt-0.5">Skipped</p>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0 space-y-0.5">
                        {r.ms != null && (
                          <div className={`text-xs font-mono font-semibold ${r.ms > 2000 ? 'text-rose-400' : r.ms > 800 ? 'text-amber-400' : 'text-emerald-400'}`}>
                            {fmtMs(r.ms)}
                          </div>
                        )}
                        {r.lastChecked && (
                          <div className="text-[10px] text-slate-500">{fmtTime(r.lastChecked)}</div>
                        )}
                        {r.status === 'pending' && (
                          <div className="text-[10px] text-slate-600">not yet run</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default HealthMonitorTab;
